// "Talk to a human" — the one behaviour, in one place.
//
// Two controls hand a Kit user to a person: the Support button in the panel
// header (kit_panel_controller#openSupport) and the "💬 Talk to a human" block
// Kit drops into the conversation when ContactSupportTool gives up
// (chat_action_controller#support). They are the same promise to the customer
// and were two copies of the same eight lines, which is how they drifted: one
// closed the panel first and the other did not, one wrote its own prefill and
// the other took Kit's.
//
// Both now call `openSupportChat` below, and the difference that mattered is
// gone: the call is VERIFIED. Intercom's messenger either appears or it does
// not, and the caller is told which, because the old shape — call a global on
// `window`, assume it worked, and delete the button — cannot tell the
// difference between a handoff and a dead click. That is the state chat 85 was
// in on 2026-09-15: the customer clicked twice, told Kit "l did that handoff
// button - not working", and no Intercom conversation was ever created. The
// same customer reached a human 2½ hours later through the header button.
//
// Kept free of `document`/`window` so test/javascript/supportHandoff.test.mjs
// can drive every branch — including "Intercom never renders" — without a
// browser. The DOM lives in the two controllers; the decision lives here.

// Where a customer goes when Intercom cannot be reached. Intercom is absent far
// more often than it looks: ApplicationController#suppress_intercom withholds it
// from the native apps, from custom domains and from impersonated sessions, the
// gem's own `exclude_if` drops some users, an ad-blocker or a privacy extension
// removes it for anyone, and Intercom's loader refuses to boot for any user
// agent matching /bot|crawler|headlesschrome/.
export const SUPPORT_EMAIL = "help@influencekit.com";

// Used when Kit called ContactSupportTool without an issue_summary. Leads with
// the explicit ask for a person so Intercom's own bot escalates rather than
// answering.
export const DEFAULT_PREFILL =
  "I need to talk to a person — Kit couldn't resolve my issue.";

export const SUBJECT = "I'd like to talk to a person";

// Intercom renders the open messenger as `.intercom-messenger-frame` inside
// `#intercom-container` (verified against frame-modern.d22d745f.js, where it
// sits at z-index 2147483002 — well above Kit's panel, so "it opened behind the
// drawer" is not a thing that can happen). The launcher frame is deliberately
// NOT in this selector: kit_chat.scss hides it with `display: none` for every
// Kit account, so its presence says nothing about whether the messenger opened.
export const MESSENGER_SELECTOR =
  "#intercom-container .intercom-messenger-frame";

// The messenger is an iframe bundle fetched on first use, so the first open on a
// page is the slow one. Four seconds is long enough for that fetch on a poor
// connection and short enough that a customer who is getting nothing finds out
// while they are still looking at the button.
export const OPEN_TIMEOUT_MS = 4000;
export const POLL_MS = 150;

// What lands in the composer: the ask Kit already gathered, then where they were
// standing when they gave up. Support gets the context without the customer
// retyping it, which is the whole point of handing over a summary.
export function supportBody(prefill, href) {
  const ask = (prefill || "").trim() || DEFAULT_PREFILL;
  return href ? `${ask}\n\n(From: ${href})` : ask;
}

export function mailtoUrl(body) {
  return (
    `mailto:${SUPPORT_EMAIL}` +
    `?subject=${encodeURIComponent(SUBJECT)}` +
    `&body=${encodeURIComponent(body)}`
  );
}

// Opens the Intercom messenger with `body` waiting in the composer, and resolves
// only once we know what happened:
//
//   { ok: true }                    the messenger is on screen
//   { ok: false, reason: "absent" } there is no Intercom on this page at all
//   { ok: false, reason: "timeout" } Intercom is here but drew nothing
//
// Deliberately does NOT call `Intercom("update", { hide_default_launcher: … })`
// first, which both callers used to do. Nothing ever sets that flag — the
// launcher is hidden in CSS precisely because the flag cannot survive
// intercom-rails reassigning `window.intercomSettings` after our script runs
// (see kit_chat.scss and intercom_unread_controller.js, which document the
// measurement) — so the call could only ever un-hide the launcher through
// Intercom's own machinery, which CSS then cannot undo. That is the stacked
// second bubble of #1104/#1296. Verified in a browser against the real widget:
// `showNewMessage` alone opens the messenger.
//
// `deps` is everything that touches the outside world, so the caller supplies
// the real ones and the test supplies fakes:
//   intercom()        -> the Intercom function, or null/undefined when absent
//   messengerVisible() -> boolean
//   setTimer(fn, ms) / clearTimer(id)
//   now()             -> milliseconds
export function openSupportChat(body, deps) {
  const {
    intercom,
    messengerVisible,
    setTimer,
    clearTimer,
    now,
    timeoutMs = OPEN_TIMEOUT_MS,
    pollMs = POLL_MS,
  } = deps;

  const send = intercom();
  if (typeof send !== "function") {
    return Promise.resolve({ ok: false, reason: "absent" });
  }

  try {
    send("showNewMessage", body);
  } catch (error) {
    // A third party throwing inside our click handler must not take the rest of
    // the handler with it — the fallback below is exactly what it is for.
    return Promise.resolve({ ok: false, reason: "absent" });
  }

  const startedAt = now();
  return new Promise((resolve) => {
    let timer = null;
    const finish = (result) => {
      if (timer !== null) clearTimer(timer);
      timer = null;
      resolve(result);
    };
    const poll = () => {
      timer = null;
      if (messengerVisible()) return finish({ ok: true });
      if (now() - startedAt >= timeoutMs) {
        return finish({ ok: false, reason: "timeout" });
      }
      timer = setTimer(poll, pollMs);
    };

    timer = setTimer(poll, pollMs);
  });
}

// The two browser-side halves of `deps`, in one place so both controllers build
// them identically. Split out rather than inlined because it is the only part
// that knows what `document` looks like, and the test never wants it.
export function browserDeps(win, doc) {
  return {
    intercom: () => win.Intercom,
    messengerVisible: () => {
      const frame = doc.querySelector(MESSENGER_SELECTOR);
      if (!frame || frame.getClientRects().length === 0) return false;
      for (let element = frame; element; element = element.parentElement) {
        const style = win.getComputedStyle(element);
        if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") return false;
      }
      return true;
    },
    setTimer: (fn, ms) => win.setTimeout(fn, ms),
    clearTimer: (id) => win.clearTimeout(id),
    now: () => Date.now(),
  };
}

// Both entry points leave a visible, copyable route to support on failure.
export function renderSupportFallback(target, body) {
  const link = target.ownerDocument.createElement("a");
  link.href = mailtoUrl(body);
  link.textContent = SUPPORT_EMAIL;
  link.className = "kit-action-buttons__fallback-link";
  target.textContent = "Couldn't open support chat. Email ";
  target.appendChild(link);
  target.append(" or try Support again. Your conversation is still here.");
  target.hidden = false;
}
